import React, { createContext, useContext, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Checkbox,
  Typography,
  TextField,
  Chip,
  Collapse,
  MenuItem,
  Select,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  LinearProgress
} from '@mui/material';
import { CheckCircle, Cancel, Image, Close, Mic, Stop, FiberManualRecord, Comment, ExpandMore, ExpandLess, Assignment, NoteAdd, Add, Remove } from '@mui/icons-material';
import api, { publicApi } from '../api';
import { useAuth } from '../components/AuthContext';
import { getFullPhotoUrl, getThumbFromItem } from '../utils/photoUrl';
import useVoiceRecording from '../hooks/useVoiceRecording';
import GeolocationInput from '../components/GeolocationInput';
import UserSelect from '../components/UserSelect';
import ObjectSelect from '../components/ObjectSelect';
import SignaturePad from '../components/SignaturePad';

const RoutineExecutionContext = createContext();

function getApiErrorMessage(error, fallbackMessage = 'Ein unbekannter Fehler ist aufgetreten') {
  const responseData = error?.response?.data;
  const directCandidates = [
    responseData?.error?.message,
    responseData?.error,
    responseData?.message,
    error?.message
  ];

  for (const candidate of directCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  if (responseData?.error && typeof responseData.error === 'object') {
    try {
      return JSON.stringify(responseData.error);
    } catch (_) {
      // ignore stringify errors and use fallback below
    }
  }

  if (error && typeof error === 'object') {
    try {
      return JSON.stringify(error);
    } catch (_) {
      return fallbackMessage;
    }
  }

  return fallbackMessage;
}

export const useRoutineExecution = () => {
  const context = useContext(RoutineExecutionContext);
  if (!context) {
    throw new Error('useRoutineExecution must be used within a RoutineExecutionProvider');
  }
  return context;
};

export const RoutineExecutionProvider = ({ children }) => {
  const { user } = useAuth();
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [executionDialogOpen, setExecutionDialogOpen] = useState(false);
  const [executingRoutine, setExecutingRoutine] = useState(null);
  const [continuingExecutionId, setContinuingExecutionId] = useState(null);
  const [stepCompletionStatus, setStepCompletionStatus] = useState({});
  const [stepAttributeValues, setStepAttributeValues] = useState({});
  const [stepStatusValues, setStepStatusValues] = useState({});
  const [stepImageFiles, setStepImageFiles] = useState({});
  const [stepImagePreviews, setStepImagePreviews] = useState({});
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState('');
  const [currentObject, setCurrentObject] = useState(null);

  // New state for notes, tasks, comments, summary
  const [executionNoteText, setExecutionNoteText] = useState('');
  const [executionNoteAudioBlob, setExecutionNoteAudioBlob] = useState(null);
  const [executionNoteDurationSec, setExecutionNoteDurationSec] = useState(0);
  const [stepComments, setStepComments] = useState({});
  const [stepTaskData, setStepTaskData] = useState({});
  const [stepTaskEnabled, setStepTaskEnabled] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [stepFreeTextValues, setStepFreeTextValues] = useState({});
  const [stepNotes, setStepNotes] = useState({}); // { stepIndex: { text, audioBlob } }
  const [stepSignatureValues, setStepSignatureValues] = useState({}); // { stepIndex: dataUrl }
  const [stepListRowData, setStepListRowData] = useState({}); // { stepIndex: { listId, cells: { colId: value } } }
  const [showSummary, setShowSummary] = useState(false);
  const [userList, setUserList] = useState([]);
  const [savingExecution, setSavingExecution] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Voice recording hook
  const {
    recordingState,
    recordingTime,
    nearLimit,
    startRecording,
    stopRecording,
    cancelRecording,
    formatRecordingTime
  } = useVoiceRecording({
    onError: (msg) => {
      setSnackbar({
        open: true,
        message: typeof msg === 'string' ? msg : getApiErrorMessage(msg, 'Fehler bei der Sprachaufnahme'),
        severity: 'error'
      });
    },
    api
  });

  // Callback functions for refreshing data
  const [refreshCallbacks, setRefreshCallbacks] = useState({});

  const executeRoutine = (routine, options = {}) => {
    console.log('🚀 Global executeRoutine called with:', routine?.title);
    setExecutingRoutine(routine);
    setContinuingExecutionId(options.continuingExecutionId || null);
    setCurrentObject(options.currentObject || null);
    setExecutionDialogOpen(true);
    setShowSummary(false);
    
    // Store refresh callbacks for later use
    setRefreshCallbacks(options.refreshCallbacks || {});

    // Fetch user list for task assignment (only for authenticated users)
    if (user) {
      api.get('/users').then(res => setUserList(res.data)).catch(() => setUserList([]));
    }
    
    // Pre-populate with existing execution data if continuing
    if (options.continuingExecutionId && options.executionData) {
      setStepCompletionStatus(options.executionData.completionStatus || {});
      setStepAttributeValues(options.executionData.attributeValues || {});
      setStepStatusValues(options.executionData.statusValues || {});
      setStepImagePreviews(options.executionData.imageValues || {});
      setStepImageFiles({});
      setStepComments(options.executionData.comments || {});
      setStepTaskData(options.executionData.taskData || {});
      // Initialize task toggles for continuing execution — default ON for steps with task data
      if (routine?.steps) {
        const enabledMap = {};
        routine.steps.forEach((step, index) => {
          if (step.taskCreation?.enabled) {
            enabledMap[index] = !!(options.executionData.taskData?.[index]?.title?.trim());
          }
        });
        setStepTaskEnabled(enabledMap);
      }
      setExecutionNoteText(options.executionData.executionNoteText || '');
      setExecutionNoteAudioBlob(null);
      setExecutionNoteDurationSec(0);
    } else {
      // Clear previous values for new execution
      setStepCompletionStatus({});
      setStepAttributeValues({});
      setStepStatusValues({});
      setStepImageFiles({});
      setStepImagePreviews({});
      setStepComments({});
      setStepTaskData({});
      setExpandedComments({});
      setStepFreeTextValues({});
      setStepNotes({});
      setStepSignatureValues({});
      setStepListRowData({}); 
      setExecutionNoteText('');
      setExecutionNoteAudioBlob(null);
      setExecutionNoteDurationSec(0);

      // Pre-initialize listRowData for steps with listCapture enabled
      if (routine?.steps) {
        const defaultListRowData = {};
        routine.steps.forEach((step, index) => {
          if (step.listCapture?.enabled && step.listCapture?.listId) {
            defaultListRowData[index] = { listId: step.listCapture.listId, cells: {} };
          }
        });
        if (Object.keys(defaultListRowData).length > 0) {
          setStepListRowData(defaultListRowData);
        }
      }

      // Pre-populate task data defaults from routine definition
      if (routine?.steps) {
        const defaultTaskData = {};
        const defaultTaskEnabled = {};
        routine.steps.forEach((step, index) => {
          if (step.taskCreation?.enabled) {
            defaultTaskData[index] = {
              title: step.taskCreation.taskTitle || '',
              description: step.taskCreation.taskDescription || '',
              assignedTo: step.taskCreation.defaultAssignee || '',
              dueDate: ''
            };
            defaultTaskEnabled[index] = true;
          }
        });
        setStepTaskData(defaultTaskData);
        setStepTaskEnabled(defaultTaskEnabled);
      }
    }
  };

  const closeDialog = () => {
    console.log('🚪 Global execution dialog closing');
    
    if (continuingExecutionId && refreshCallbacks.forceReloadExecutions) {
      console.log('🔄 Calling forceReloadExecutions because continuingExecutionId is set');
      refreshCallbacks.forceReloadExecutions();
    }

    // Cancel any ongoing recording
    if (recordingState === 'recording') {
      cancelRecording();
    }
    
    setExecutionDialogOpen(false);
    setContinuingExecutionId(null);
    setExecutingRoutine(null);
    setStepCompletionStatus({});
    setStepAttributeValues({});
    setStepStatusValues({});
    setStepImageFiles({});
    setStepImagePreviews({});
    setStepComments({});
    setStepTaskData({});
    setStepTaskEnabled({});
    setExpandedComments({});
    setStepFreeTextValues({});
    setStepNotes({});
    setStepSignatureValues({});
    setStepListRowData({});
    setExecutionNoteText('');
    setExecutionNoteAudioBlob(null);
    setExecutionNoteDurationSec(0);
    setShowSummary(false);
    setSavingExecution(false);
    setRefreshCallbacks({});
  };

  // Determine which steps are visible (conditional step logic)
  const visibleSteps = useMemo(() => {
    if (!executingRoutine?.steps) return [];
    return executingRoutine.steps.map((step, index) => {
      if (!step.condition?.dependsOnStep && step.condition?.dependsOnStep !== 0) return { ...step, _index: index, _visible: true };
      const depIndex = step.condition.dependsOnStep;
      const depStatus = stepStatusValues[depIndex];
      const showWhen = step.condition.showWhen || 'not_ok';
      const isVisible = depStatus === showWhen;
      return { ...step, _index: index, _visible: isVisible };
    });
  }, [executingRoutine?.steps, stepStatusValues]);

  // Look up attribute type by name from the current object's attributeSections
  const getAttributeType = (attributeName) => {
    if (!attributeName || !currentObject?.attributeSections) return 'string';
    for (const section of currentObject.attributeSections) {
      const attr = (section.attributes || []).find(a => a.name === attributeName);
      if (attr) return attr.type || 'string';
    }
    return 'string';
  };

  // Check if all required visible steps are completed
  const allRequiredComplete = useMemo(() => {
    return visibleSteps.every(step => {
      if (!step._visible) return true;
      if (step.required === false) return true;
      return stepCompletionStatus[step._index] === true;
    });
  }, [visibleSteps, stepCompletionStatus]);

  // Progress percentage
  const progressPercent = useMemo(() => {
    const visible = visibleSteps.filter(s => s._visible);
    if (visible.length === 0) return 0;
    const completed = visible.filter(s => stepCompletionStatus[s._index]).length;
    return Math.round((completed / visible.length) * 100);
  }, [visibleSteps, stepCompletionStatus]);

  // Track if user has entered any data (for cancel confirmation)
  const hasUnsavedData = useMemo(() => {
    return Object.values(stepCompletionStatus).some(v => v) ||
      Object.values(stepAttributeValues).some(v => v?.trim()) ||
      Object.values(stepStatusValues).some(v => v) ||
      Object.values(stepImageFiles).some(v => v) ||
      Object.values(stepComments).some(v => v?.trim()) ||
      Object.values(stepFreeTextValues).some(v => v?.trim()) ||
      Object.values(stepNotes).some(v => v?.text?.trim() || v?.audioBlob) ||
      Object.values(stepSignatureValues).some(v => v) ||
      executionNoteText?.trim();
  }, [stepCompletionStatus, stepAttributeValues, stepStatusValues, stepImageFiles, stepComments, stepFreeTextValues, stepNotes, stepSignatureValues, executionNoteText]);

  // Handle voice recording stop → get blob
  const handleStopRecording = async () => {
    const recordingResult = await stopRecording();
    if (recordingResult?.audioBlob) {
      setExecutionNoteAudioBlob(recordingResult.audioBlob);
      setExecutionNoteDurationSec(recordingResult.durationSec || 0);
    }
    // stopRecording() leaves recordingState in 'processing' — reset it
    cancelRecording();
  };

  const handleSaveExecution = async () => {
    if (!executingRoutine || savingExecution) return;
    
    setSavingExecution(true);
    try {
      // Prepare step executions data
      const stepExecutions = executingRoutine.steps.map((step, index) => ({
        stepIndex: index,
        stepTitle: step.title,
        completed: stepCompletionStatus[index] || false,
        attributeValue: stepAttributeValues[index] || null,
        attributeName: step.attribute || null,
        statusValue: stepStatusValues[index] || null,
        imageValue: null,
        comment: stepComments[index] || null,
        freeTextValue: stepFreeTextValues[index] || null,
        signatureValue: stepSignatureValues[index] || null,
        listRowData: stepListRowData[index] || null,
        taskData: (step.taskCreation?.enabled && stepTaskEnabled[index]) ? (stepTaskData[index] || null) : null
      }));
      
      const formData = new FormData();
      formData.append('stepExecutions', JSON.stringify(stepExecutions));
      formData.append('notes', '');
      
      // Add execution note text
      if (executionNoteText?.trim()) {
        formData.append('executionNoteText', executionNoteText);
      }

      // Add voice recording audio blob
      if (executionNoteAudioBlob) {
        formData.append('audio_note', executionNoteAudioBlob, 'voice.webm');
        if (Number.isFinite(executionNoteDurationSec) && executionNoteDurationSec > 0) {
          formData.append('audio_note_durationSec', String(Math.round(executionNoteDurationSec)));
        }
      }
      
      // Add step-level note texts and audio blobs
      Object.keys(stepNotes).forEach(stepIndex => {
        const note = stepNotes[stepIndex];
        if (note?.text?.trim()) {
          formData.append(`stepNoteText_${stepIndex}`, note.text);
        }
        if (note?.audioBlob) {
          formData.append(`audio_note_step_${stepIndex}`, note.audioBlob, `step_note_${stepIndex}.webm`);
        }
      });
      
      // Add step images
      Object.keys(stepImageFiles).forEach(stepIndex => {
        const file = stepImageFiles[stepIndex];
        if (file) {
          const renamedFile = new File(
            [file],
            `routine-${executingRoutine._id}-step-${stepIndex}-${Date.now()}.jpg`,
            { type: file.type }
          );
          formData.append(`image_${stepIndex}`, renamedFile);
        }
      });
      
      let response;
      const isVisitor = !user && currentObject?.userRole === 'visitor';
      const apiClient = isVisitor ? publicApi : api;
      
      if (continuingExecutionId) {
        if (isVisitor) {
          throw new Error('Visitors cannot update existing executions');
        }
        response = await api.put(`/routines/execution/${continuingExecutionId}`, formData);
        
        if (response.data?.stepExecutions) {
          const newPreviews = {};
          response.data.stepExecutions.forEach(stepExec => {
            if (stepExec.imageValue && stepExec.stepIndex !== undefined) {
              newPreviews[stepExec.stepIndex] = stepExec.imageValue;
            }
          });
          setStepImagePreviews(prev => ({ ...prev, ...newPreviews }));
        }
      } else {
        const endpoint = isVisitor 
          ? `/routines/public/${executingRoutine._id}/execute`
          : `/routines/${executingRoutine._id}/execute`;
        response = await apiClient.post(endpoint, formData);
      }
      
      // Refresh data
      const promises = [];
      if (refreshCallbacks.refreshRoutines) promises.push(refreshCallbacks.refreshRoutines());
      if (refreshCallbacks.refreshObject) promises.push(refreshCallbacks.refreshObject());
      if (refreshCallbacks.forceReloadExecutions) refreshCallbacks.forceReloadExecutions();
      if (refreshCallbacks.reloadOverdueRoutines) refreshCallbacks.reloadOverdueRoutines();
      await Promise.all(promises);
      
      // Show warnings from backend if any
      const responseWarnings = response.data?.warnings;
      if (responseWarnings?.length > 0) {
        setSnackbar({ open: true, message: responseWarnings.join('. '), severity: 'warning' });
      }
      
      closeDialog();
      
    } catch (error) {
      console.error('Error executing routine:', error);
      setSnackbar({
        open: true,
        message: `Fehler beim Speichern: ${getApiErrorMessage(error)}`,
        severity: 'error'
      });
    } finally {
      setSavingExecution(false);
    }
  };

  const contextValue = {
    executeRoutine,
    closeDialog,
    executionDialogOpen,
    executingRoutine
  };

  // Helper to render a single step's content
  const renderStepContent = (step, index) => (
    <>
      {step.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {step.description}
        </Typography>
      )}
      {step.image && (
        <Box sx={{ mb: 1 }}>
          <img 
            src={getThumbFromItem(step, 256)} 
            alt="Schritt Bild"
            loading="lazy"
            style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', cursor: 'pointer', borderRadius: '4px' }}
            onClick={() => { setPreviewImageSrc(getFullPhotoUrl(step.image)); setImagePreviewOpen(true); }}
          />
        </Box>
      )}
      {step.attribute && (() => {
        const attrType = getAttributeType(step.attribute);
        const attrValue = stepAttributeValues[index];
        const setAttrValue = (val) => setStepAttributeValues(prev => ({ ...prev, [index]: val }));
        
        if (attrType === 'date') return (
          <TextField label={`Wert für ${step.attribute}`} type="date" size="small" fullWidth sx={{ mt: 1 }}
            InputLabelProps={{ shrink: true }}
            value={attrValue || ''}
            onChange={(e) => setAttrValue(e.target.value)}
          />
        );
        if (attrType === 'geolocation') return (
          <Box sx={{ mt: 1 }}>
            <GeolocationInput label={`Wert für ${step.attribute}`} value={attrValue || null} onChange={setAttrValue} fullWidth />
          </Box>
        );
        if (attrType === 'bool' || attrType === 'binary') return (
          <FormControlLabel sx={{ mt: 1 }}
            control={<Checkbox size="small" checked={attrValue === '1' || attrValue === true}
              onChange={(e) => setAttrValue(e.target.checked ? '1' : '0')} />}
            label={step.attribute}
          />
        );
        if (attrType === 'int') return (
          <TextField label={`Wert für ${step.attribute}`} type="number" size="small" fullWidth sx={{ mt: 1 }}
            inputProps={{ step: 1 }}
            value={attrValue ?? ''}
            onChange={(e) => setAttrValue(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
          />
        );
        if (attrType === 'float' || attrType === 'euro') return (
          <TextField label={`Wert für ${step.attribute}${attrType === 'euro' ? ' (€)' : ''}`} type="number" size="small" fullWidth sx={{ mt: 1 }}
            inputProps={{ step: 0.01 }}
            value={attrValue ?? ''}
            onChange={(e) => setAttrValue(e.target.value === '' ? '' : parseFloat(e.target.value))}
          />
        );
        if (attrType === 'user') return (
          <Box sx={{ mt: 1 }}>
            <UserSelect label={`Wert für ${step.attribute}`} value={attrValue || null} onChange={setAttrValue} />
          </Box>
        );
        if (attrType === 'object') return (
          <Box sx={{ mt: 1 }}>
            <ObjectSelect label={`Wert für ${step.attribute}`} value={attrValue || null} onChange={setAttrValue} />
          </Box>
        );
        if (attrType === 'counter') return (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => setAttrValue(Math.max(0, (parseInt(attrValue) || 0) - 1))}><Remove fontSize="small" /></IconButton>
            <TextField label={step.attribute} type="number" size="small" sx={{ width: 120 }}
              inputProps={{ step: 1, min: 0 }}
              value={attrValue ?? ''}
              onChange={(e) => setAttrValue(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
            />
            <IconButton size="small" onClick={() => setAttrValue((parseInt(attrValue) || 0) + 1)}><Add fontSize="small" /></IconButton>
          </Box>
        );
        if (attrType === 'email') return (
          <TextField label={`Wert für ${step.attribute}`} type="email" size="small" fullWidth sx={{ mt: 1 }}
            value={attrValue || ''} onChange={(e) => setAttrValue(e.target.value)}
          />
        );
        if (attrType === 'phone') return (
          <TextField label={`Wert für ${step.attribute}`} type="tel" size="small" fullWidth sx={{ mt: 1 }}
            value={attrValue || ''} onChange={(e) => setAttrValue(e.target.value)}
          />
        );
        if (attrType === 'hyperlink') return (
          <TextField label={`Wert für ${step.attribute}`} type="url" size="small" fullWidth sx={{ mt: 1 }}
            placeholder="https://..."
            value={attrValue || ''} onChange={(e) => setAttrValue(e.target.value)}
          />
        );
        // Default: string
        return (
          <TextField label={`Wert für ${step.attribute}`} size="small" fullWidth sx={{ mt: 1 }}
            value={attrValue || ''} onChange={(e) => setAttrValue(e.target.value)}
          />
        );
      })()}
      {step.statusCheck && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Status:</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={stepStatusValues[index] === 'ok' ? 'contained' : 'outlined'}
              color="success" size="small"
              onClick={() => setStepStatusValues(prev => ({ ...prev, [index]: stepStatusValues[index] === 'ok' ? '' : 'ok' }))}
              startIcon={<CheckCircle />}
            >OK</Button>
            <Button
              variant={stepStatusValues[index] === 'not_ok' ? 'contained' : 'outlined'}
              color="error" size="small"
              onClick={() => setStepStatusValues(prev => ({ ...prev, [index]: stepStatusValues[index] === 'not_ok' ? '' : 'not_ok' }))}
              startIcon={<Cancel />}
            >Nicht OK</Button>
          </Box>
        </Box>
      )}
      {step.requiresImage && (
        <Box sx={{ mt: 1 }}>
          <input
            type="file" accept="image/*" style={{ display: 'none' }}
            id={`step-execution-image-${index}`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setStepImageFiles(prev => ({ ...prev, [index]: file }));
                setStepImagePreviews(prev => ({ ...prev, [index]: URL.createObjectURL(file) }));
              }
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button variant="outlined" size="small" startIcon={<Image />}
              onClick={() => document.getElementById(`step-execution-image-${index}`)?.click()}
            >Bild hochladen</Button>
            {stepImagePreviews[index] && (
              <Button size="small" color="error" onClick={() => {
                if (stepImagePreviews[index]?.startsWith('blob:')) URL.revokeObjectURL(stepImagePreviews[index]);
                setStepImageFiles(prev => { const u = { ...prev }; delete u[index]; return u; });
                setStepImagePreviews(prev => { const u = { ...prev }; delete u[index]; return u; });
              }}>Entfernen</Button>
            )}
          </Box>
          {stepImagePreviews[index] && (
            <Box sx={{ mt: 1 }}>
              <img
                src={stepImagePreviews[index].startsWith('blob:') ? stepImagePreviews[index] : getFullPhotoUrl(stepImagePreviews[index])}
                alt="Schritt Bild"
                style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                onClick={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  const src = stepImagePreviews[index].startsWith('blob:') ? stepImagePreviews[index] : getFullPhotoUrl(stepImagePreviews[index]);
                  setPreviewImageSrc(src); setImagePreviewOpen(true);
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Signature capture */}
      {step.signature && (
        <SignaturePad
          value={stepSignatureValues[index] || null}
          onChange={(dataUrl) => setStepSignatureValues(prev => ({ ...prev, [index]: dataUrl }))}
          label="Unterschrift"
        />
      )}

      {/* Task creation fields for steps with taskCreation enabled */}
      {step.taskCreation?.enabled && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: stepTaskEnabled[index] ? 1 : 0 }}>
            <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
              <Assignment fontSize="small" /> Aufgabe erfassen
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!stepTaskEnabled[index]}
                  onChange={(e) => setStepTaskEnabled(prev => ({ ...prev, [index]: e.target.checked }))}
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>
          {stepTaskEnabled[index] && (
            <>
          <TextField
            label="Aufgabentitel"
            size="small" fullWidth sx={{ mb: 1 }}
            value={stepTaskData[index]?.title || ''}
            onChange={(e) => setStepTaskData(prev => ({
              ...prev, [index]: { ...(prev[index] || {}), title: e.target.value }
            }))}
          />
          <TextField
            label="Beschreibung"
            size="small" fullWidth multiline rows={2} sx={{ mb: 1 }}
            value={stepTaskData[index]?.description || ''}
            onChange={(e) => setStepTaskData(prev => ({
              ...prev, [index]: { ...(prev[index] || {}), description: e.target.value }
            }))}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {userList.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                <InputLabel>Zuweisen an</InputLabel>
                <Select
                  label="Zuweisen an"
                  value={stepTaskData[index]?.assignedTo || ''}
                  onChange={(e) => setStepTaskData(prev => ({
                    ...prev, [index]: { ...(prev[index] || {}), assignedTo: e.target.value }
                  }))}
                >
                  <MenuItem value="">Niemand</MenuItem>
                  {userList.map(u => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Fällig am"
              type="date"
              size="small"
              sx={{ flex: 1, minWidth: 150 }}
              InputLabelProps={{ shrink: true }}
              value={stepTaskData[index]?.dueDate || ''}
              onChange={(e) => setStepTaskData(prev => ({
                ...prev, [index]: { ...(prev[index] || {}), dueDate: e.target.value }
              }))}
            />
          </Box>
            </>
          )}
        </Box>
      )}

      {/* Step comment (collapsible) */}
      <Box sx={{ mt: 1 }}>
        <Button
          size="small" sx={{ textTransform: 'none', color: 'text.secondary', p: 0, minWidth: 'auto' }}
          startIcon={<Comment fontSize="small" />}
          endIcon={expandedComments[index] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          onClick={() => setExpandedComments(prev => ({ ...prev, [index]: !prev[index] }))}
        >
          Kommentar {stepComments[index] ? '✓' : ''}
        </Button>
        <Collapse in={expandedComments[index]}>
          <TextField
            placeholder="Kommentar zu diesem Schritt..."
            size="small" fullWidth multiline rows={2} sx={{ mt: 0.5 }}
            value={stepComments[index] || ''}
            onChange={(e) => setStepComments(prev => ({ ...prev, [index]: e.target.value }))}
          />
        </Collapse>
      </Box>

      {/* Free-text field */}
      {step.freeText && (
        <TextField
          label="Freitext"
          placeholder="Text eingeben..."
          size="small"
          fullWidth
          multiline
          rows={3}
          sx={{ mt: 1 }}
          value={stepFreeTextValues[index] || ''}
          onChange={(e) => setStepFreeTextValues(prev => ({ ...prev, [index]: e.target.value }))}
        />
      )}

      {/* Step-level notes (text + voice) */}
      {step.notesEnabled && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <NoteAdd fontSize="small" /> Schritt-Notiz
          </Typography>
          <TextField
            placeholder="Notiz zu diesem Schritt..."
            size="small" fullWidth multiline rows={2} sx={{ mb: 1 }}
            value={stepNotes[index]?.text || ''}
            onChange={(e) => setStepNotes(prev => ({ ...prev, [index]: { ...(prev[index] || {}), text: e.target.value } }))}
          />
          {stepNotes[index]?.audioBlob && (
            <Chip label="🎤 Sprachaufnahme bereit" size="small" color="success" variant="outlined" sx={{ mb: 0.5 }} />
          )}
        </Box>
      )}

      {/* List row capture */}
      {step.listCapture?.enabled && step.listCapture?.listId && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid', borderColor: '#90caf9' }}>
          <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            📋 Listenzeile erfassen
          </Typography>
          {(() => {
            const list = currentObject?.lists?.find(l => l.id === step.listCapture.listId);
            if (!list) return <Typography variant="caption" color="error">Liste nicht gefunden</Typography>;
            const captureColumns = step.listCapture.columns || [];
            const columnsToShow = captureColumns.length > 0 
              ? list.columns.filter(c => captureColumns.some(cc => cc.columnId === c.id))
              : list.columns;
            return columnsToShow.map(col => {
              const isRequired = captureColumns.find(cc => cc.columnId === col.id)?.required;
              const currentValue = stepListRowData[index]?.cells?.[col.id];
              const updateCell = (value) => setStepListRowData(prev => ({
                ...prev,
                [index]: {
                  listId: step.listCapture.listId,
                  cells: { ...(prev[index]?.cells || {}), [col.id]: value }
                }
              }));
              
              if (col.type === 'boolean' || col.type === 'binary') {
                return (
                  <FormControlLabel
                    key={col.id}
                    control={<Checkbox size="small" checked={currentValue === true || currentValue === '1'} onChange={(e) => updateCell(e.target.checked)} />}
                    label={<Typography variant="body2">{col.name}{isRequired ? ' *' : ''}</Typography>}
                    sx={{ mb: 0.5 }}
                  />
                );
              }
              if (col.type === 'date') {
                return (
                  <TextField key={col.id} label={col.name} type="date" size="small" fullWidth sx={{ mb: 1 }}
                    InputLabelProps={{ shrink: true }} required={isRequired}
                    value={currentValue || ''} onChange={(e) => updateCell(e.target.value)}
                  />
                );
              }
              if (col.type === 'int') {
                return (
                  <TextField key={col.id} label={col.name} type="number" size="small" fullWidth sx={{ mb: 1 }}
                    required={isRequired} inputProps={{ step: 1 }}
                    value={currentValue ?? ''} onChange={(e) => updateCell(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                  />
                );
              }
              if (col.type === 'float' || col.type === 'euro') {
                return (
                  <TextField key={col.id} label={`${col.name}${col.type === 'euro' ? ' (€)' : ''}`} type="number" size="small" fullWidth sx={{ mb: 1 }}
                    required={isRequired} inputProps={{ step: 0.01 }}
                    value={currentValue ?? ''} onChange={(e) => updateCell(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  />
                );
              }
              if (col.type === 'counter') {
                return (
                  <Box key={col.id} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton size="small" onClick={() => updateCell(Math.max(0, (parseInt(currentValue) || 0) - 1))}><Remove fontSize="small" /></IconButton>
                    <TextField label={col.name} type="number" size="small" sx={{ width: 120 }}
                      inputProps={{ step: 1, min: 0 }} required={isRequired}
                      value={currentValue ?? ''} onChange={(e) => updateCell(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    />
                    <IconButton size="small" onClick={() => updateCell((parseInt(currentValue) || 0) + 1)}><Add fontSize="small" /></IconButton>
                  </Box>
                );
              }
              if (col.type === 'geolocation') {
                return (
                  <Box key={col.id} sx={{ mb: 1 }}>
                    <GeolocationInput label={`${col.name}${isRequired ? ' *' : ''}`} value={currentValue || null} onChange={updateCell} fullWidth />
                  </Box>
                );
              }
              if (col.type === 'user') {
                return (
                  <Box key={col.id} sx={{ mb: 1 }}>
                    <UserSelect label={`${col.name}${isRequired ? ' *' : ''}`} value={currentValue || null} onChange={updateCell} />
                  </Box>
                );
              }
              if (col.type === 'object') {
                return (
                  <Box key={col.id} sx={{ mb: 1 }}>
                    <ObjectSelect label={`${col.name}${isRequired ? ' *' : ''}`} value={currentValue || null} onChange={updateCell} />
                  </Box>
                );
              }
              if (col.type === 'email') {
                return (
                  <TextField key={col.id} label={col.name} type="email" size="small" fullWidth sx={{ mb: 1 }}
                    required={isRequired} value={currentValue || ''} onChange={(e) => updateCell(e.target.value)}
                  />
                );
              }
              if (col.type === 'phone') {
                return (
                  <TextField key={col.id} label={col.name} type="tel" size="small" fullWidth sx={{ mb: 1 }}
                    required={isRequired} value={currentValue || ''} onChange={(e) => updateCell(e.target.value)}
                  />
                );
              }
              if (col.type === 'hyperlink') {
                return (
                  <TextField key={col.id} label={col.name} type="url" size="small" fullWidth sx={{ mb: 1 }}
                    required={isRequired} placeholder="https://..." value={currentValue || ''} onChange={(e) => updateCell(e.target.value)}
                  />
                );
              }
              // Default: string
              return (
                <TextField key={col.id} label={col.name} size="small" fullWidth sx={{ mb: 1 }}
                  required={isRequired} value={currentValue || ''} onChange={(e) => updateCell(e.target.value)}
                />
              );
            });
          })()}
        </Box>
      )}
    </>
  );

  // Summary view renderer
  const renderSummary = () => (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Zusammenfassung</Typography>
      {visibleSteps.filter(s => s._visible).map((step) => {
        const idx = step._index;
        const completed = stepCompletionStatus[idx];
        return (
          <Box key={idx} sx={{ mb: 1.5, p: 1, bgcolor: completed ? 'success.50' : 'grey.50', borderRadius: 1, border: '1px solid', borderColor: completed ? 'success.light' : 'grey.300' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {completed ? <CheckCircle color="success" fontSize="small" /> : <Cancel color="disabled" fontSize="small" />}
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{step.title}</Typography>
              {step.required === false && <Chip label="Optional" size="small" variant="outlined" sx={{ ml: 'auto', height: 20 }} />}
            </Box>
            {stepAttributeValues[idx] != null && stepAttributeValues[idx] !== '' && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                {step.attribute}: {(() => {
                  const val = stepAttributeValues[idx];
                  if (val && typeof val === 'object') {
                    if (val.lat !== undefined) return `🌍 ${val.lat?.toFixed?.(4)}, ${val.lng?.toFixed?.(4)}${val.address ? ` (${val.address})` : ''}`;
                    if (val.firstName || val.lastName) return `👤 ${val.firstName || ''} ${val.lastName || ''}`.trim();
                    if (val.name) return `🔗 ${val.name}`;
                    return JSON.stringify(val);
                  }
                  return String(val);
                })()}
              </Typography>
            )}
            {stepStatusValues[idx] && (
              <Chip label={stepStatusValues[idx] === 'ok' ? '✓ OK' : '✗ Nicht OK'} size="small"
                color={stepStatusValues[idx] === 'ok' ? 'success' : 'error'} sx={{ ml: 4, mt: 0.5, height: 20 }} />
            )}
            {stepComments[idx] && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', fontStyle: 'italic' }}>
                💬 {stepComments[idx]}
              </Typography>
            )}
            {stepTaskData[idx]?.title && step.taskCreation?.enabled && stepTaskEnabled[idx] && (
              <Typography variant="caption" color="primary" sx={{ ml: 4, display: 'block' }}>
                📋 Aufgabe: {stepTaskData[idx].title}
              </Typography>
            )}
            {step.taskCreation?.enabled && !stepTaskEnabled[idx] && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                📋 Aufgabe: deaktiviert
              </Typography>
            )}
            {stepFreeTextValues[idx] && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', whiteSpace: 'pre-wrap' }}>
                📝 {stepFreeTextValues[idx]}
              </Typography>
            )}
            {stepNotes[idx]?.text && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                📝 Notiz: {stepNotes[idx].text}
              </Typography>
            )}
            {stepNotes[idx]?.audioBlob && (
              <Chip label="🎤 Schritt-Sprachaufnahme" size="small" sx={{ ml: 4, mt: 0.5, height: 20 }} />
            )}
            {stepListRowData[idx]?.cells && Object.values(stepListRowData[idx].cells).some(v => v != null && v !== '') && (
              <Typography variant="caption" color="primary" sx={{ ml: 4, display: 'block' }}>
                📋 Listenzeile: {Object.values(stepListRowData[idx].cells).filter(v => v != null && v !== '').length} Felder erfasst
              </Typography>
            )}
            {stepImagePreviews[idx] && (
              <Box sx={{ ml: 4, mt: 0.5 }}>
                <img
                  src={stepImagePreviews[idx].startsWith('blob:') ? stepImagePreviews[idx] : getFullPhotoUrl(stepImagePreviews[idx])}
                  alt="Schritt Bild"
                  style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const src = stepImagePreviews[idx].startsWith('blob:') ? stepImagePreviews[idx] : getFullPhotoUrl(stepImagePreviews[idx]);
                    setPreviewImageSrc(src);
                    setImagePreviewOpen(true);
                  }}
                />
              </Box>
            )}
            {stepSignatureValues[idx] && (
              <Box sx={{ ml: 4, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">✍️ Unterschrift:</Typography>
                <img src={stepSignatureValues[idx]} alt="Unterschrift" style={{ maxWidth: '180px', maxHeight: '80px', display: 'block', border: '1px solid #e0e0e0', borderRadius: '4px', background: '#fff', marginTop: 2 }} />
              </Box>
            )}
          </Box>
        );
      })}

      {/* Notes summary */}
      {executingRoutine?.notesEnabled && (executionNoteText?.trim() || executionNoteAudioBlob) && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
          <Typography variant="caption" color="info.main" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <NoteAdd fontSize="small" /> Notiz
          </Typography>
          {executionNoteText?.trim() && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>{executionNoteText}</Typography>
          )}
          {executionNoteAudioBlob && (
            <Chip label="🎤 Sprachaufnahme vorhanden" size="small" sx={{ mt: 0.5, height: 20 }} />
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <RoutineExecutionContext.Provider value={contextValue}>
      {children}
      
      {/* Global Routine Execution Dialog */}
      <Dialog 
        open={executionDialogOpen} 
        onClose={() => {
          if (hasUnsavedData && !showSummary) {
            if (window.confirm('Möchten Sie wirklich abbrechen? Nicht gespeicherte Daten gehen verloren.')) {
              closeDialog();
            }
          } else {
            closeDialog();
          }
        }} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
          {showSummary ? 'Überprüfen & Speichern' : `${executingRoutine?.title} ausführen`}
          {isMobile && (
            <IconButton onClick={() => {
              if (hasUnsavedData && !showSummary) {
                if (window.confirm('Möchten Sie wirklich abbrechen? Nicht gespeicherte Daten gehen verloren.')) {
                  closeDialog();
                }
              } else {
                closeDialog();
              }
            }} edge="end" aria-label="close">
              <Close />
            </IconButton>
          )}
        </DialogTitle>
        {!showSummary && executingRoutine && (
          <Box sx={{ px: 3, pt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinearProgress variant="determinate" value={progressPercent} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>{progressPercent}%</Typography>
            </Box>
          </Box>
        )}
        <DialogContent>
          {executingRoutine && !showSummary && (
            <Box sx={{ mt: 1 }}>
              <Stepper orientation="vertical">
                {visibleSteps.filter(s => s._visible).map((step) => {
                  const index = step._index;
                  return (
                    <Step key={index} active={true}>
                      <StepLabel
                        optional={step.required === false ? <Typography variant="caption" color="text.secondary">Optional</Typography> : undefined}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox 
                            checked={stepCompletionStatus[index] || false}
                            onChange={(e) => setStepCompletionStatus(prev => ({ ...prev, [index]: e.target.checked }))}
                            sx={{ 
                              transform: 'scale(1.3)',
                              '& .MuiSvgIcon-root': { fontSize: 28 },
                              color: 'grey.400',
                              '&.Mui-checked': { color: 'success.main' },
                              p: 0.5
                            }}
                          />
                          <Typography>{step.title}</Typography>
                        </Box>
                      </StepLabel>
                      <StepContent>
                        {renderStepContent(step, index)}
                      </StepContent>
                    </Step>
                  );
                })}
              </Stepper>

              {/* Execution Notes Section */}
              {executingRoutine.notesEnabled && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <NoteAdd fontSize="small" color="primary" /> Notizen
                  </Typography>
                  <TextField
                    placeholder="Notizen zur Ausführung..."
                    size="small" fullWidth multiline rows={3} sx={{ mb: 1 }}
                    value={executionNoteText}
                    onChange={(e) => setExecutionNoteText(e.target.value)}
                  />
                  
                  {/* Voice recording controls */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {recordingState === 'idle' && (
                      <Button variant="outlined" size="small" startIcon={<Mic />} onClick={startRecording}>
                        Sprachnotiz aufnehmen
                      </Button>
                    )}
                    {recordingState === 'recording' && (
                      <>
                        <Button variant="contained" color="error" size="small" startIcon={<Stop />} onClick={handleStopRecording}>
                          Aufnahme beenden
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FiberManualRecord sx={{ color: 'error.main', fontSize: 14, animation: 'blink 1s infinite' }} />
                          <Typography variant="caption" color={nearLimit ? 'error' : 'text.secondary'}>
                            {formatRecordingTime(recordingTime)}
                          </Typography>
                        </Box>
                      </>
                    )}
                    {recordingState === 'processing' && (
                      <Typography variant="caption" color="text.secondary">Verarbeite Aufnahme...</Typography>
                    )}
                    {executionNoteAudioBlob && recordingState === 'idle' && (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label="🎤 Sprachaufnahme bereit" size="small" color="success" variant="outlined" />
                        <Button size="small" color="error" onClick={() => setExecutionNoteAudioBlob(null)}>
                          Entfernen
                        </Button>
                      </Box>
                    )}
                  </Box>
                  {executingRoutine.noteSection && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Wird gespeichert in Sektion: „{executingRoutine.noteSection}"
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Summary View */}
          {executingRoutine && showSummary && renderSummary()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (hasUnsavedData && !showSummary) {
              if (window.confirm('Möchten Sie wirklich abbrechen? Nicht gespeicherte Daten gehen verloren.')) {
                closeDialog();
              }
            } else {
              closeDialog();
            }
          }}>Abbrechen</Button>
          {showSummary ? (
            <>
              <Button onClick={() => setShowSummary(false)} disabled={savingExecution}>Zurück</Button>
              <Button variant="contained" onClick={handleSaveExecution} disabled={savingExecution}>
                {savingExecution ? 'Speichert...' : 'Speichern'}
              </Button>
            </>
          ) : (
            <Button 
              variant="contained" 
              onClick={() => setShowSummary(true)}
              disabled={!allRequiredComplete}
            >
              Weiter
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={imagePreviewOpen} onClose={() => setImagePreviewOpen(false)} maxWidth="lg">
        <DialogContent sx={{ p: 0 }}>
          {previewImageSrc && (
            <img
              src={previewImageSrc}
              alt="Vorschau"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagePreviewOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for warnings/errors */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </RoutineExecutionContext.Provider>
  );
};